import { useState, useRef, useLayoutEffect } from "react";
import { Asterisk } from "lucide-react";
import Reveal from "@/components/Reveal";

/* Learn → Practice → Build → Earn — Figma node 107:3955 (Frame 174), 1423 × 182.

   Each pill has two states — photo + label, and lime note + copy — stacked and
   cross-faded so it never reflows on hover.

   Which one shows by default comes from the layer order in the Figma component:
   Frames 17 (Learn), 29 (Practice) and 30 (Earn) put the photo on top, Frame 27
   (Build) puts the note on top. Hence Build alone is open by default. */

const STAGE_W = 1423;
const STAGE_H = 182;

// Stacked layout only — one pastel per step.
const STEP_BG = ["var(--lime)", "#A9EDE6", "#D2D8FF", "#FFD1E9"];

const ITEMS = [
  {
    key: "learn",
    label: "Learn",
    text: "Discover in-demand skills from industry experts.",
    img: "/images/journey-learn.jpg",
    defaultState: "photo",
    x: 0,
    w: 353,
  },
  {
    key: "practice",
    label: "Practice",
    text: "Apply your knowledge through real-world projects.",
    img: "/images/journey-practice.jpg",
    defaultState: "photo",
    x: 357,
    w: 352,
  },
  {
    key: "build",
    label: "Build",
    text: "Build your brand, create content and get noticed.",
    // NOTE: stand-in. Frame 27's photo variant was never exported from Figma —
    // the instance in Frame 174 renders its lime state, so only that came back.
    img: "/images/testimonial-creator.jpg",
    defaultState: "note",
    x: 713,
    w: 353,
  },
  {
    key: "earn",
    label: "Earn",
    text: "Unlock opportunities and grow your career.",
    img: "/images/journey-earn.jpg",
    defaultState: "photo",
    x: 1070,
    w: 353,
  },
];

/* Both states are lg+ only — the stacked layout renders its own combined card. */
function PhotoState({ item }) {
  return (
    <>
      <img src={item.img} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <span className="absolute inset-0 bg-black/20" aria-hidden />
      <span
        className="absolute inset-0 flex items-center justify-center font-poppins text-white"
        style={{ fontSize: 58, letterSpacing: "2.9px" }}
      >
        {item.label}
      </span>
    </>
  );
}

function NoteState({ item }) {
  return (
    <span className="absolute inset-0 flex items-center gap-5 bg-[var(--lime)] pl-[46px] pr-[40px]">
      <Asterisk className="h-16 w-16 shrink-0 text-black" strokeWidth={2.6} aria-hidden />
      <span className="font-poppins text-black" style={{ fontSize: 16, lineHeight: "19px" }}>
        {item.text}
      </span>
    </span>
  );
}

function Pill({ item, width, className = "" }) {
  const Default = item.defaultState === "note" ? NoteState : PhotoState;
  const Hover = item.defaultState === "note" ? PhotoState : NoteState;
  return (
    <div
      className={`group relative shrink-0 overflow-hidden rounded-[100px] ${className}`}
      style={{ width, height: STAGE_H }}
    >
      <div className="absolute inset-0 transition-opacity duration-300 ease-out group-hover:opacity-0">
        <Default item={item} />
      </div>
      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100">
        <Hover item={item} />
      </div>
    </div>
  );
}

export default function JourneyStrip() {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <section className="mx-auto w-full max-w-[1900px] px-5 py-12 lg:px-3 lg:py-8 xl:py-10">

      {/* ── desktop stage ── */}
      <div
        ref={stageRef}
        className="relative hidden w-full lg:block"
        style={{ height: scale ? STAGE_H * scale : undefined }}
      >
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: `scale(${scale || 1})`,
            visibility: scale ? "visible" : "hidden",
          }}
        >
          {ITEMS.map((it) => (
            <div key={it.key} className="absolute top-0" style={{ left: it.x }}>
              <Pill item={it} width={it.w} />
            </div>
          ))}
        </div>
      </div>

      {/* Touch has no hover, so each step carries the photo, label and text at
          once rather than hiding half behind a state swap. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
        {ITEMS.map((it, i) => (
          <Reveal key={it.key} delay={i * 70} className="h-full">
            <div
              className="flex h-full items-center gap-4 rounded-[100px] p-2.5 pr-6"
              style={{ background: STEP_BG[i % STEP_BG.length] }}
            >
              <span className="relative h-[86px] w-[86px] shrink-0 overflow-hidden rounded-full">
                <img src={it.img} alt="" className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute inset-0 bg-black/15" aria-hidden />
                <span className="absolute inset-0 flex items-center justify-center font-montserrat text-[26px] font-semibold text-white">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </span>

              <span className="flex min-w-0 flex-col gap-1.5">
                <span className="flex items-center gap-2">
                  <Asterisk className="h-5 w-5 shrink-0 text-black" strokeWidth={2.6} aria-hidden />
                  <span className="font-poppins text-[21px] leading-none tracking-[1.5px] text-black">
                    {it.label}
                  </span>
                </span>
                <span className="font-poppins text-[13.5px] leading-[19px] text-black/70">
                  {it.text}
                </span>
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
